import { NonTermViewTypeEnum } from "@/ts/enums/NonTermViewTypeEnum";
import { GradeSubjectFeedbackFormData } from "@/ts/interfaces/Reporting/api/GradeSubjectFeedbackApiInterface";
import { FeedbackFormData } from "@/ts/interfaces/Reporting/api/StudentFeedbackApiDetailInterface";
import { SubjectFeedbackForGradeFormData } from "@/ts/interfaces/Reporting/api/SubjectFeedbackForGradeApiInterface";
import { mapPayloadDataForFeedbackSubmission } from "@/utils/reportingDataMapper";
import type {
    CurriculumConnection,
    LearnosityRequest,
    Material,
    TermBasedMessage
} from "@shared/learnosity";

import { apiClient } from "./apiClient";
import { getCacheBuster, refreshCacheBuster } from "./cacheBuster";

export interface LessonRuntimeData {
    lesson_id: number;
    activity_uuid: string;
    activity_request: {
        message: string;
        metadata: {
            activity_id: string;
            domain: string;
            generated_at: string;
            session_id: string;
            user_id: string;
        };
        request: LearnosityRequest;
        success: boolean;
    } | null;
    curriculum_connection?: CurriculumConnection | null;
    learning_objectives?: string[];
    list_of_materials?: Material[];
    term_based_messages?: TermBasedMessage[];
    status?: string;
    has_completed_activity_items?: boolean;
    activity_item_uuids?: Record<string, string>;
    apply_activity_item_uuids?: Record<string, string>;
    reflection_activity_item_uuids?: Record<string, string>;
    learn_activity_item_uuids?: { learn: string };
    textbook_activity_item_uuids?: { textbook: string };
}

export const getStudentEntitlementsApi = (parentId: number) => {
    return apiClient.get(
        route("reports.student.entitlements", { parent: parentId.toString() })
    );
};

export const getLessionDetailsByGradeApi = (
    eukaId: string,
    gradeCode: number,
    courseIDs: number[]
) => {
    return apiClient.post(route("reports.student.courses-details"), {
        euka_id: eukaId,
        course_ids: courseIDs,
        grade_code: gradeCode
    });
};

export const getQuizDetailsByGradeCodeApi = (
    eukaId: string,
    gradeCode: number
) => {
    return apiClient.post(route("reports.student.quiz-details"), {
        euka_id: eukaId,
        grade_code: gradeCode
    });
};

export const getQuizResultsByActivityIdApi = (
    eukaId: string,
    activityId: string
) => {
    return apiClient.post(route("reports.student.quiz-result"), {
        euka_id: eukaId,
        activity_id: activityId
    });
};

export const getAssignmentMarksByGradeApi = (
    eukaId: string,
    courseIDs: number[]
) => {
    return apiClient.post(
        route("reports.student.assignment-marks", {
            student: eukaId.toString()
        }),
        {
            course_ids: courseIDs
        }
    );
};

export const getStudentSubjectsApi = (studentId: number, gradeCode: number) => {
    return apiClient.get(
        route("reports.student.subjects", {
            student: studentId.toString(),
            grade_code: gradeCode.toString()
        })
    );
};

export const getWeekLessonsDetailsApi = (
    eukaId: string,
    courseIds: number[],
    gradeSubjectId: number,
    term: number
) => {
    return apiClient.post(route("reports.week-lessons-details"), {
        euka_id: eukaId,
        course_ids: courseIds,
        grade_subject_id: gradeSubjectId,
        term
    });
};

export const getCourseIdListByLicenseAndGradeApi = (
    licenseId: number,
    gradeCode: number
) => {
    return apiClient.get(
        route("reports.license.courses", {
            license: licenseId.toString(),
            grade_code: gradeCode.toString()
        })
    );
};

export const getSubjectFeedbackWithAnswersApi = (
    studentId: number,
    familyGradeId: number,
    term: number | null,
    subjectId: number,
    type: NonTermViewTypeEnum,
    personalLearningPlanId?: number | null
) => {
    const params: Record<string, string> = {
        student: studentId.toString(),
        product_family_grade: familyGradeId.toString(),
        term: term !== null ? term.toString() : "999",
        subjectId: subjectId.toString(),
        type
    };

    if (personalLearningPlanId != null) {
        params.personal_learning_plan_id = personalLearningPlanId.toString();
    }

    return apiClient.get(
        route("reports.student.feedback-subject-details", params)
    );
};

export const getCourseFeedbackWithAnswersApi = (
    studentId: number,
    familyGradeId: number,
    term: number | null,
    courseId: number,
    type: NonTermViewTypeEnum,
    personalLearningPlanId?: number | null
) => {
    const params: Record<string, string> = {
        student: studentId.toString(),
        product_family_grade: familyGradeId.toString(),
        term: term !== null ? term.toString() : "999",
        courseId: courseId.toString(),
        type
    };

    if (personalLearningPlanId != null) {
        params.personal_learning_plan_id = personalLearningPlanId.toString();
    }

    return apiClient.get(
        route("reports.student.feedback-course-details", params)
    );
};

export const getGradeSubjectFeedbackWithAnswersApi = (
    studentId: number,
    familyGradeId: number,
    isPremiumEntitlement: boolean
) => {
    const params: Record<string, string> = {
        student: studentId.toString(),
        product_family_grade: familyGradeId.toString()
    };

    const routeUrl = isPremiumEntitlement
        ? "reports.student.grade.premium.feedback"
        : "reports.student.grade.feedback";

    return apiClient.get(route(routeUrl, params));
};

export const getSubjectFeedbackForGradeWithAnswersApi = (
    studentId: number,
    familyGradeId: number,
    subjectId: number,
    personalLearningPlanId: number | null
) => {
    const params: Record<string, string | null> = {
        student: studentId.toString(),
        product_family_grade: familyGradeId.toString(),
        subjectId: subjectId.toString()
    };

    if (personalLearningPlanId != null) {
        params.personal_learning_plan_id = personalLearningPlanId.toString();
    }

    return apiClient.get(
        route("reports.student.grade.feedback-subject-details", params)
    );
};

export const getAllFeedbackDetailsApi = (
    studentId: number,
    familyGradeId: number,
    term: number | null,
    type: NonTermViewTypeEnum
) => {
    const suffix = `STUDENT_${studentId}_FEEDBACK`;
    const cacheBuster = getCacheBuster(suffix);

    const params: Record<string, string> = {
        student: studentId.toString(),
        product_family_grade: familyGradeId.toString(),
        term: term !== null ? term.toString() : "999",
        type
    };

    if (cacheBuster) {
        params.cache_buster = cacheBuster;
    }

    return apiClient.get(route("reports.student.all-feedback-details", params));
};

export const getLessonCurriculumOutcomesApi = (lessonId: number) => {
    return apiClient.get(
        route("reports.lesson.curriculum-outcomes", {
            lessonId: lessonId.toString()
        })
    );
};

export const getStudentFeedbackDetailsApi = (
    studentId: number,
    familyGradeId: number,
    term: number | null,
    type: NonTermViewTypeEnum
) => {
    return apiClient.get(
        route("reports.student.feedback-details", {
            student: studentId.toString(),
            product_family_grade: familyGradeId.toString(),
            term: term !== null ? term.toString() : "999",
            type
        })
    );
};

export const getStudentAssignmentDetailsApi = (
    studentId: number,
    uuid: string
) => {
    return apiClient.get(
        route("reports.details-assignment-get", {
            assignment: uuid,
            student: studentId.toString()
        })
    );
};

export const submitStudentFeedbackApi = (data: FeedbackFormData) => {
    return apiClient
        .post(
            route("reports.student.feedback-submission", {
                student: data.student_id
            }),
            mapPayloadDataForFeedbackSubmission(data)
        )
        .then(res => {
            const suffix = `STUDENT_${data.student_id}_FEEDBACK`;
            refreshCacheBuster(suffix);
            return res;
        });
};

export const submitGradeSubjectFeedbackApi = (
    data: GradeSubjectFeedbackFormData
) => {
    return apiClient
        .post(
            route("reports.student.feedback-submission", {
                student: data.student_id
            }),
            data
        )
        .then(res => {
            const suffix = `STUDENT_${data.student_id}_GRADE_${data.product_family_grade_id}_FEEDBACK`;
            refreshCacheBuster(suffix);
            return res;
        });
};

export const submitSubjectFeedbackForGradeApi = (
    data: SubjectFeedbackForGradeFormData
) => {
    return apiClient
        .post(
            route("reports.student.feedback-submission", {
                student: data.student_id
            }),
            data
        )
        .then(res => {
            const suffix = `STUDENT_${data.student_id}__SUBJECT_${data.subject_id}_GRADE_${data.product_family_grade_id}_FEEDBACK`;
            refreshCacheBuster(suffix);
            return res;
        });
};

export const getAcademicTranscriptPreviewDataApi = (
    studentId: number,
    productFamilyGradeId: number,
    term: string
) => {
    const suffix = `STUDENT_${studentId}_FEEDBACK`;
    const cacheBuster = getCacheBuster(suffix);

    const params: Record<string, string> = {
        student: studentId.toString(),
        productFamilyGradeId: productFamilyGradeId.toString(),
        term
    };

    if (cacheBuster) {
        params.cache_buster = cacheBuster;
    }

    return apiClient.get(
        route("api.student.academic-transcript.preview", params)
    );
};

export const getAcademicTranscriptGradePreviewDataApi = (
    studentId: number,
    productFamilyGradeId: number
) => {
    const suffix = `STUDENT_${studentId}_GRADE_FEEDBACK`;
    const cacheBuster = getCacheBuster(suffix);
    console.log(studentId, productFamilyGradeId);

    const params: Record<string, string> = {
        student: studentId.toString(),
        productFamilyGradeId: productFamilyGradeId.toString()
    };

    if (cacheBuster) {
        params.cache_buster = cacheBuster;
    }

    return apiClient.get(
        route("api.student.academic-transcript.grade-preview", params)
    );
};

export const generateAcademicTranscriptApi = (
    studentId: number,
    productFamilyGradeId: number,
    gradeCode: number,
    term: number,
    year: number
) => {
    return apiClient.post(
        route("reports.generate-academic-transcript", {
            student: studentId.toString()
        }),
        {
            product_family_grade_id: productFamilyGradeId,
            grade_code: gradeCode,
            term,
            year
        }
    );
};

export const generateGradeAcademicTranscriptApi = (
    studentId: number,
    productFamilyGradeId: number,
    year: number
) => {
    return apiClient.post(
        route("reports.generate-grade.academic-transcript", {
            student: studentId.toString()
        }),
        {
            product_family_grade_id: productFamilyGradeId,
            year
        }
    );
};

export const getLatestAcademicTranscriptApi = (
    studentId: number,
    familyGradeId: number,
    term: number
) => {
    return apiClient.get(
        route("api.student.academic-transcript.latest", {
            student: studentId,
            productFamilyGradeId: familyGradeId,
            term: term.toString()
        })
    );
};

export const downloadAcademicTranscriptApi = (asset: number) => {
    return apiClient.get(
        route("api.student.academic-transcript.download", {
            asset: asset.toString()
        })
    );
};

export const createInitialWorkSampleSubmission = (
    sudentId: number,
    lessonId: number | null,
    subjectId: number | null,
    productFamilyGradeId: number,
    term: number | null,
    personalLearningPlanId: number | null,
    courseId?: number | null,
    assignmentUUId?: string | null
) => {
    const data = {
        lesson_id: lessonId,
        subject_id: subjectId,
        course_id: courseId,
        product_family_grade_id: productFamilyGradeId,
        personal_learning_plan_id: personalLearningPlanId,
        term,
        assignment_uuid: assignmentUUId
    };

    return apiClient.post(
        route("reports.work-sample.create", { student: sudentId }),
        data
    );
};

export const createAssetForWorkSampleSubmission = (
    uuid: string,
    file: File,
    filePefix: string
) => {
    const data = {
        file_size: file.size,
        file_name: file.name,
        mime_type: file.type,
        file_prefix: filePefix
    };

    return apiClient.post(
        route("reports.work-sample.asset.create", { uuid }),
        data
    );
};

export const uploadFileForSubmission = (file: File, url: string) => {
    return apiClient.put(url, file, {
        headers: {
            "Content-Type": file.type
        }
    });
};

export const deleteSubmissionAsset = (asset: string) => {
    return apiClient.delete(
        route("reports.work-sample.asset.delete", {
            asset
        })
    );
};

export const updateWorkSampleApi = (
    uuid: string,
    data: {
        feedback: string;
        comment: string;
    }
) => {
    return apiClient.post(route("reports.work-sample.update", { uuid }), data);
};

export const getWorkSampleByUuid = (uuid: string) => {
    return apiClient.get(
        route("reports.work-sample", {
            uuid
        })
    );
};

export const createWorkSampleHighlightApi = (
    studentId: number,
    productFamilyGradeId: number,
    type: string
) => {
    const data = {
        product_family_grade_id: productFamilyGradeId,
        type
    };

    return apiClient.post(
        route("reports.work-sample.create-highlight", {
            student: studentId
        }),
        data
    );
};

export const getGradeLatestAcademicTranscriptApi = (
    studentId: number,
    productFamilyGradeId: number
) => {
    return apiClient.get(
        route("api.student.academic-transcript.grade.latest", {
            student: studentId,
            productFamilyGradeId
        })
    );
};

export const getArchiveDetailsApi = () => {
    return apiClient.get(route("reports.archive.list"));
};

export const getStudentCertificateApi = (
    studentId: number,
    productFamilyGradeId: number,
    type: string,
    term?: number,
    coursId?: number,
    subjectId?: number
) => {
    const baseUrl = route("reports.student.certificate.show", {
        student: studentId.toString(),
        product_family_grade: productFamilyGradeId.toString(),
        type
    });

    const query = new URLSearchParams();

    if (term) {
        query.append("term", term.toString());
    }

    if (coursId) {
        query.append("course_id", coursId.toString());
    }

    if (subjectId) {
        query.append("subject_id", subjectId.toString());
    }

    const finalUrl = query.toString()
        ? `${baseUrl}?${query.toString()}`
        : baseUrl;

    return apiClient.get(finalUrl);
};

export const getAssessedTranscriptStatusApi = (
    studentAuth0Username: string,
    gradeId: number
) => {
    return apiClient.get(
        route("reports.assessed-transcript.status", {
            student: studentAuth0Username,
            gradeId: gradeId.toString()
        })
    );
};

export const compileAssessedTranscriptApi = (
    academicYear: number,
    studentId: number,
    gradeId: number
) => {
    return apiClient.post(
        route("reports.assessed-transcript.compile", {
            student: studentId
        }),
        {
            academic_year: academicYear,
            grade_id: gradeId,
            product_family_grade_id: gradeId
        }
    );
};

export const downloadAssessedTranscriptApi = (
    studentId: number,
    gradeId: number
) => {
    return apiClient.post(
        route("reports.assessed-transcript.download", {
            student: studentId
        }),
        {
            grade_id: gradeId,
            product_family_grade_id: gradeId
        }
    );
};

export const getLessonDetailsApi = (lessonId: number) => {
    return apiClient.get(
        route("reports.lesson.details", {
            lessonId: lessonId.toString()
        })
    );
};

export const getLessonRuntimeApi = (studentId: number, lessonId: number) => {
    return apiClient.get(
        route("api.student.lesson-catalogue.runtime", {
            student: studentId.toString(),
            lesson: lessonId.toString()
        })
    );
};
